import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import { workOrdersApiKeyStore } from '../../../../api/workorders-api-key-store';
import { ApiResponse, ApiResponseForPost, ApiResponseWithPagination } from '../../../../models/api-response';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { WorkorderItem } from '../../../workorders/workorders-tab/workorder-item.component';
import { Button, FormControl, Grid, MenuItem, Pagination, Select } from '@mui/material';
import { CreateWorkorderDialog } from '../../../workorders/create-workorder/create-workorder-dialog';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { GuardedComponent } from '../../../../components/guarded-component';
import { Unit } from '../../../../models/unit';
import { ReactHookForm } from '../../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../../hooks/useReactHookForm';
import { FormAutoCompleteMultiSelectDynamic } from '../../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { servicesApiKeyStore } from '../../../../api/masters/services-api-key-store';
import { useSaveDataForPost } from '../../../../hooks/useSaveData';
import { FormAutoCompleteSelectDynamic } from '../../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { serviceGroupApiKeyStore } from '../../../../api/masters/service-group-api-key-store';
import { useSearchParams } from 'react-router-dom';

interface WorkOrdersTabProps {
  unit: Unit;
  refetchUnit: () => void;
}

const TEMP_DATA = [
  {
    value: 'all',
    label: 'All',
    isDefault: true,
  },
  {
    value: 'others',
    label: 'Original Inspection',
    isDefault: false,
  },
  {
    value: 'retest',
    label: 'Retest Inspection',
    isDefault: false,
  },
];
const ITEMS_PER_PAGE = 50;

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    service: params.get('service'),
    serviceGroupID: Number(params.get('serviceGroupID')),
    retestService: params.get('retestService'),

    pageNumber: params.get('pageNumber') ?? 1,
    pageSize: params.get('pageSize') ?? 50,
  };
};
export const WorkOrdersTab = ({ unit, refetchUnit }: WorkOrdersTabProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(
    !getFiltesFromUrl(searchParams)?.pageNumber ? 1 : getFiltesFromUrl(searchParams)?.pageNumber
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    !getFiltesFromUrl(searchParams)?.pageSize ? ITEMS_PER_PAGE : getFiltesFromUrl(searchParams)?.pageSize
  );
  const [workOrderData, setWorkOrderData] = useState<any>();
  const [selectedFilters, setSelectedFilters] = useState<any>({
    unitID: unit?.unitID,
    customerID: unit?.customerID,
    services: getFiltesFromUrl(searchParams)?.service
      ? getFiltesFromUrl(searchParams)!
        .service!.split(',')
        .map((id: string) => (!id ? 0 : Number(id)))
      : [],
    retestService: !getFiltesFromUrl(searchParams)?.retestService
      ? 'all'
      : getFiltesFromUrl(searchParams)?.retestService,
    serviceGroupID: getFiltesFromUrl(searchParams)?.serviceGroupID,
    pageNumber: !getFiltesFromUrl(searchParams)?.pageNumber ? currentPage : getFiltesFromUrl(searchParams)?.pageNumber,
    pageSize: !getFiltesFromUrl(searchParams)?.pageSize ? itemsPerPage : getFiltesFromUrl(searchParams)?.pageSize,
  });

  const form = useReactHookForm({
    defaultValues: {
      retestService: {
        value: !getFiltesFromUrl(searchParams)?.retestService ? 'all' : getFiltesFromUrl(searchParams)?.retestService,
      },
      service: getFiltesFromUrl(searchParams)?.service
        ? getFiltesFromUrl(searchParams)!
          .service!.split(',')
          .map((id: string) => ({ serviceID: Number(id), serviceName: '' }))
        : [],
      serviceGroupName: { serviceGroupID: getFiltesFromUrl(searchParams)?.serviceGroupID, serviceGroupName: '' },
    },
  });

  // const { isLoading, isFetching, data, refetch } = useLoadData<ApiResponseWithPagination<any>>(
  //   workOrdersApiKeyStore.getUnitWorkOrdersWithPagination(unit?.unitID, currentPage, itemsPerPage)
  // );

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);

  const { data, isLoading, mutateAsync } = useSaveDataForPost<ApiResponseForPost<any>>(
    workOrdersApiKeyStore.getWorkOrderByFilters()
  );

  useEffect(() => {
    if (data) setWorkOrderData(data?.data);
  }, [data]);

  useEffect(() => {
    mutateAsync({ data: selectedFilters });
  }, [selectedFilters]);

  const handleAddWorkOrder = () => {
    setIsFormOpen(true);
  };
  const handleDialogClose = () => {
    setIsFormOpen(false);
  };

  const refetchData = () => {
    mutateAsync({ data: selectedFilters });
  };

  useEffect(() => {
    if (getFiltesFromUrl(searchParams)) {
      // refetchData();
    }
  }, [getFiltesFromUrl(searchParams)]);

  const handleWorkOrderRefresh = (value: boolean) => {
    if (value) {
      mutateAsync({ data: selectedFilters });
      handleDialogClose();
    }
  };

  const handlePageChange = (event: any, value: number) => {
    setCurrentPage(value);
    setSelectedFilters((prevFilters: any) => ({
      ...prevFilters,
      pageNumber: Number(value),
    }));
    setSearchParams((prevFilters: URLSearchParams) => {
      const filtersFromUrl = getFiltesFromUrl(searchParams) || {};

      return {
        pageSize: String(filtersFromUrl.pageSize || ITEMS_PER_PAGE), // Ensure it's a string
        service: selectedFilters?.services?.join(',') || '',
        pageNumber: String(value), // Convert number to string
        retestService: selectedFilters.retestService ? selectedFilters.retestService : '',
        serviceGroupID: selectedFilters.serviceGroupID ? String(selectedFilters.serviceGroupID) : '',
      };
    });
    // handlePageNumberOrPageSizeChange?.(value, selectedFilters?.pageSize || ITEMS_PER_PAGE); // Reset to first page when items per page change
  };

  const handleItemsPerPageChange = (event: any) => {
    setSelectedFilters((prevFilters: any) => ({
      ...prevFilters,
      pageNumber: 1,
      pageSize: Number(event.target.value),
    }));
    setItemsPerPage(event.target.value as number);
    setCurrentPage(1);

    setSearchParams({
      service: selectedFilters?.services?.join(',') || '', // Ensure it's a string
      pageNumber: '1', // Convert number to string
      pageSize: String(event.target.value), // Convert number to string
      retestService: selectedFilters.retestService ? '' : selectedFilters.retestService,
      serviceGroupID: selectedFilters.serviceGroupID ? String(selectedFilters.serviceGroupID) : '',
    });
    // handlePageNumberOrPageSizeChange?.(1, event.target.value as number); // Reset to first page when items per page change
  };

  useEffect(() => {
    mutateAsync({ data: selectedFilters });
  }, [currentPage, itemsPerPage]);

  const handleSubmit = (filtersData: any) => {
    setSelectedFilters((prevFilters: any) => ({
      ...prevFilters,
      pageNumber: 1,
      services: filtersData?.service?.map((p: any) => p.serviceID),
      retestService: !filtersData?.retestService?.value ? '' : filtersData?.retestService?.value,
      serviceGroupID: filtersData?.serviceGroupName?.serviceGroupID,
    }));

    setSearchParams({
      ...(filtersData?.retestService?.value && { retestService: filtersData?.retestService?.value }),
      ...(filtersData?.serviceGroupName?.serviceGroupID && {
        serviceGroupID: filtersData?.serviceGroupName?.serviceGroupID,
      }),
      ...(filtersData?.service?.length && {
        service: filtersData?.service?.map((p: any) => p.serviceID).join(','),
      }),

      pageNumber: String(1),
      pageSize: selectedFilters?.pageSize.toString(),
    });
  };

  const handleFooter = (pageNumber: any, pageSize: any, totalRecords: any) => {
    const startIndex = (pageNumber - 1) * pageSize + 1;
    const endIndex = Math.min(pageNumber * pageSize, totalRecords);
    const footerText = `${startIndex} - ${endIndex} of ${totalRecords} ${totalRecords <= 1 ? 'item' : 'items'}`;
    return footerText;
  }

  return (
    <>
      <div className="borderTop">
        {!unit?.isCompleted && (
          <GuardedComponent requiredPermissions={[UserPermissionName.ADD_WORKORDER]}>
            <Button
              className="innerTab-button"
              size="small"
              variant="contained"
              disableElevation={true}
              startIcon={<AddIcon />}
              onClick={handleAddWorkOrder}
            >
              Add Work Order
            </Button>
          </GuardedComponent>
        )}
        <Grid container spacing={1}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <ReactHookForm
              formName="DETAIL_UNIT_WORK_ORDER_FILTER_FORM"
              onSubmit={handleSubmit}
              formContextProps={form}
            >
              <Grid container spacing={1}>
                <Grid item lg={4} sm={4} xl={4} xs={4}>
                  <FormAutoCompleteMultiSelectDynamic
                    name="service"
                    label="Service"
                    getQueryKeyConfig={() => servicesApiKeyStore.getServices()}
                    optionsLabelKey="serviceName"
                    optionsValueKey="serviceID"
                  />
                </Grid>
                <Grid item lg={4} sm={4} xl={4} xs={4}>
                  <FormAutoCompleteSelectDynamic
                    name="retestService"
                    label="Inspection Type"
                    // onChange={() => {
                    //   hanldeChangeWorkOrderID();
                    // }}
                    dataItems={TEMP_DATA}
                    optionsLabelKey="label"
                    optionsValueKey="value"
                  />
                </Grid>
                <Grid item lg={4} sm={4} xl={4} xs={4}>
                  <FormAutoCompleteSelectDynamic
                    name="serviceGroupName"
                    label="Service Group"
                    getQueryKeyConfig={() => serviceGroupApiKeyStore.getServiceGroups()}
                    optionsLabelKey="serviceGroupName"
                    optionsValueKey="serviceGroupID"
                  />
                </Grid>
                <Grid item xl={3} lg={3} sm={3} xs={6}>
                  <Button type="submit" title="Filter" variant="contained" startIcon={<FilterAltOutlinedIcon />}>
                    Filter
                  </Button>
                </Grid>
              </Grid>
            </ReactHookForm>
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <LoadingIndicator isLoading={isLoading} />
            {workOrderData?.map((item: any) => (
              <WorkorderItem isCompleted={!item?.canEdit} key={item.workOrderID} item={item} reloadData={refetchData} refetchUnit={refetchUnit} />
            ))}
          </Grid>
        </Grid>
      </div>
      <div>
        <LoadingIndicator isLoading={isLoading} />
        {workOrderData?.length > 0 && (
          <Grid style={{ padding: '20px 0px 15px' }} container spacing={0}>
            <Grid item lg={10} sm={10} xl={10} xs={10}>

              <Grid
                container
                direction="row"
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                }}
              >
                <Pagination
                  count={data?.totalPages}
                  page={!currentPage ? 1 : Number(currentPage)}
                  onChange={handlePageChange}
                  variant="outlined"
                  shape="rounded"
                />
                <FormControl className="pagingperSelect" variant="outlined">
                  {/* <InputLabel id="items-per-page-label">Items per page</InputLabel> */}
                  <Select
                    labelId="items-per-page-label"
                    id="items-per-page-select"
                    value={!itemsPerPage ? '50' : String(itemsPerPage)}
                    onChange={handleItemsPerPageChange}
                    label=" "
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                  <span>items per page</span>
                </FormControl>
              </Grid>
            </Grid>
            <Grid item lg={2} sm={2} xl={2} xs={2} style={{ paddingTop: 8 }}>
              <Grid
                container
                direction="row"
                sx={{
                  justifyContent: "flex-end",
                  alignItems: "flex-start",
                }}
              >
                <span>{handleFooter(data?.pageNumber, data?.pageSize, data?.totalRecords)}</span>
                {/* <span>{selectedFilters?.pageNumber || 1} - {selectedFilters?.pageSize || ITEMS_PER_PAGE} of {filteredData?.length} {`${filteredData?.length <= 1 ? 'item' : 'items'}`}</span> */}
              </Grid>
            </Grid>
          </Grid>
        )}
      </div>
      <CreateWorkorderDialog
        handleWorkOrderRefresh={handleWorkOrderRefresh}
        open={isFormOpen}
        onClose={handleDialogClose}
        selectedFilters={{
          customerID: unit?.customerID,
          officeID: unit?.officeID,
          projectID: unit?.projectID,
          unitID: unit?.unitID,
        }}
      />
      {/* <SiTrackGrid
        columns={columns}
        dataItemKey="workOrderID"
        queryConfig={workOrdersApiKeyStore.getUnitWorkOrders(unitID)}
        onEdit={handleEdit}
        deleteMutationConfig={(deleteItem) => workOrdersApiKeyStore.deleteWorkOrder(deleteItem.workOrderID)}
      />
      {editItem && <WorkOrderEditDialog handleEditClose={handleEditClose} workOrder={editItem} unitID={unitID} />} */}
    </>
  );
};
